import Header from "@/sections/Header";
import Footer from "@/sections/Footer";
import { Link } from "react-router-dom";

const SECTIONS = [
  {
    id: "what-we-collect",
    title: "1. What we collect",
    body: (
      <>
        <p>We collect the minimum needed to run the product:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>
            <strong>Coin you create</strong> — name, ticker, supply, theme, description, optional creator wallet
            address, AI-generated logo and whitepaper text.
          </li>
          <li>
            <strong>Email</strong> — only if you sign up for the early-holder airdrop list.
          </li>
          <li>
            <strong>Hashed IP address</strong> — first 16 hex chars of SHA-256(your IP), used only to dedupe likes,
            tip intents, and creator records. We do <strong>not</strong> store your raw IP.
          </li>
          <li>
            <strong>Public wallet address</strong> — if you link a Phantom wallet. Publicly displayed on
            your coin page if you set it as the tip recipient.
          </li>
          <li>
            <strong>Payment info</strong> — handled entirely by Stripe. We see the last 4 digits of your card and
            country only, never the full number.
          </li>
          <li>
            <strong>Aggregated, non-identifying analytics</strong> — page views, coin views, likes, tip counts.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "what-we-dont",
    title: "2. What we don't collect",
    body: (
      <ul className="list-disc pl-6 space-y-1">
        <li>We do <strong>not</strong> store your raw IP address.</li>
        <li>We do <strong>not</strong> see, store, or transmit your wallet private keys.</li>
        <li>We do <strong>not</strong> read browsing history, location, microphone, camera, or contacts.</li>
        <li>We do <strong>not</strong> sell, rent, or share your personal data with advertisers.</li>
        <li>We do <strong>not</strong> use third-party advertising trackers or "fingerprinting" pixels.</li>
      </ul>
    ),
  },
  {
    id: "cookies",
    title: "3. Cookies and local storage",
    body: (
      <>
        <p>
          We use <strong>localStorage</strong> (not cookies) for a few non-tracking purposes:
        </p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Your paper-trading wallet ID and compliance checklist progress.</li>
          <li>Whether you've connected Phantom before (so we can auto-reconnect on revisit).</li>
          <li>Which coins you've liked (so the heart stays filled in your browser).</li>
        </ul>
        <p className="mt-3">
          None of this is sent to third parties. You can clear it any time via your browser settings.
        </p>
      </>
    ),
  },
  {
    id: "third-parties",
    title: "4. Third parties we use",
    body: (
      <>
        <p>These vendors process data on our behalf:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li><strong>Stripe</strong> — payments. See <a className="text-[#cfff04] underline" href="https://stripe.com/privacy" target="_blank" rel="noreferrer">stripe.com/privacy</a>.</li>
          <li><strong>Helius</strong> — Solana RPC for reading wallet balances and verifying tip transactions.</li>
          <li><strong>Kraken</strong> — public market data only (no PII).</li>
          <li><strong>Emergent (Claude, Gemini Nano Banana)</strong> — AI generation for logos and whitepapers. Your coin's name, ticker, and theme are sent to the model; not your personal data.</li>
          <li><strong>MongoDB Atlas / Railway / Vercel</strong> — hosting and database.</li>
        </ul>
      </>
    ),
  },
  {
    id: "retention",
    title: "5. How long we keep data",
    body: (
      <ul className="list-disc pl-6 space-y-1">
        <li>
          <strong>Published coin pages</strong> — kept indefinitely while live. Once a coin is part of public web
          history, the marketing page is part of that record.
        </li>
        <li>
          <strong>Draft coins</strong> — auto-purged after 30 days of inactivity.
        </li>
        <li>
          <strong>Payment records</strong> — kept for 7 years to comply with tax and anti-fraud regulations
          (US, EU, UK).
        </li>
        <li>
          <strong>Email airdrop list</strong> — until you unsubscribe (a one-click link in every email).
        </li>
        <li>
          <strong>Hashed IPs</strong> — kept until the related coin / like / tip is deleted.
        </li>
      </ul>
    ),
  },
  {
    id: "your-rights",
    title: "6. Your rights",
    body: (
      <>
        <p>Depending on your jurisdiction (GDPR, CCPA, UK GDPR, LGPD), you have the right to:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Access a copy of your personal data we hold.</li>
          <li>Correct inaccurate data.</li>
          <li>Delete a draft coin (yourself) or a published coin (by emailing us).</li>
          <li>Unsubscribe from any email list with one click.</li>
          <li>Object to processing or request restriction.</li>
        </ul>
        <p className="mt-3">
          Email <a className="text-[#cfff04] underline" href="mailto:privacy@solspur.com">privacy@solspur.com</a>{" "}
          from the address you signed up with. We respond within 30 days (usually within 48 hours).
        </p>
      </>
    ),
  },
  {
    id: "children",
    title: "7. Children's privacy",
    body: (
      <p>
        SolSpur is not directed to children under 13 (or 16 in the EU). We do not knowingly collect personal data
        from minors. If you believe a minor has provided data to us, email us and we'll delete it.
      </p>
    ),
  },
  {
    id: "security",
    title: "8. Security",
    body: (
      <p>
        We use TLS encryption in transit, encrypted MongoDB connections, server-side payment processing (we never
        see card numbers), GDPR-style IP hashing, and unique-index protections to prevent duplicate signups or
        like-fraud. We're a small team and not a billion-dollar bank — please don't store anything on SolSpur that
        you couldn't tolerate becoming public.
      </p>
    ),
  },
  {
    id: "intl",
    title: "9. International transfers",
    body: (
      <p>
        Our hosting providers (Vercel, Railway, MongoDB Atlas) operate globally. By using SolSpur from outside the
        United States, you consent to your data being processed in the US and other countries where these vendors
        operate. All vendors are GDPR-aligned with appropriate Standard Contractual Clauses.
      </p>
    ),
  },
  {
    id: "changes",
    title: "10. Changes to this policy",
    body: (
      <p>
        We'll bump the "last updated" date below and announce material changes on the homepage. Continued use
        after 7 days constitutes acceptance.
      </p>
    ),
  },
];

export default function Privacy() {
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <main className="pt-24 pb-20" data-testid="privacy-page">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#cfff04] mb-3">
            // Legal
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter mb-2">
            Privacy Policy
          </h1>
          <p className="text-[#e2e2e2] mb-10">Last updated: 27 May 2026</p>

          <div className="space-y-10 text-sm leading-relaxed text-[#efefef]">
            {SECTIONS.map((s) => (
              <section key={s.id} id={s.id} data-testid={`privacy-${s.id}`}>
                <h2 className="font-display text-xl font-bold uppercase tracking-tight text-white mb-3">
                  {s.title}
                </h2>
                <div className="space-y-2">{s.body}</div>
              </section>
            ))}
          </div>

          <div className="mt-16 pt-8 border-t border-[#3a3a3a] flex flex-wrap items-center justify-between gap-4">
            <Link
              to="/terms"
              className="font-mono text-xs uppercase tracking-widest text-[#e2e2e2] hover:text-[#cfff04]"
              data-testid="privacy-to-tos"
            >
              ← Read Terms of Service
            </Link>
            <a
              href="mailto:privacy@solspur.com"
              className="font-mono text-xs uppercase tracking-widest text-[#e2e2e2] hover:text-[#cfff04]"
            >
              privacy@solspur.com
            </a>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
